/**
 * Encrypt one file under a fresh media key and upload it as E2E media.
 *
 * Shared by the media outbox (DM and Space channel sends) and Space
 * announcements. The caller embeds the returned attachment (which carries the
 * key) in an encrypted payload, and uploads the moderation scan copy after
 * the message or announcement is accepted.
 *
 * @module services/e2eMediaFileUpload
 */

import type { createApiClient } from '@adieuu/shared';
import { encrypt as encryptBytes, randomBytes, toBase64 } from '@adieuu/crypto';
import { stripExifMetadata } from '../utils/imageProcessing';
import { withTimeout } from '../utils/withTimeout';
import {
  isVisualMediaFile,
  prepareConversationMediaFileForUpload,
  uploadE2EMediaOnly,
  type ModerationScanPayload,
} from './conversationMediaUploadFlow';
import type { MediaAttachment } from './messagePayload';
import {
  MEDIA_OUTBOX_ATTACHMENT_PIPELINE_TIMEOUT_MS,
  MEDIA_OUTBOX_PREPARE_TIMEOUT_MS,
} from './mediaOutbox/mediaOutboxConstants';

export interface E2eMediaFileUploadOptions {
  /** Strip EXIF from images before encrypting. */
  stripExif: boolean;
  /** When false, no moderation scan copy is produced. */
  moderationEnabled: boolean;
  signal: AbortSignal;
  t: (key: string, fallback: string, opts?: Record<string, unknown>) => string;
}

export interface E2eMediaFileUploadResult {
  attachment: MediaAttachment & { sizeBytes: number };
  /** Absent for non-visual files and when moderation is off. */
  moderationScan?: ModerationScanPayload | ModerationScanPayload[];
}

export async function uploadE2eMediaFile(
  api: ReturnType<typeof createApiClient>,
  rawFile: File,
  options: E2eMediaFileUploadOptions,
): Promise<E2eMediaFileUploadResult> {
  const { signal, t } = options;
  const isVisual = isVisualMediaFile(rawFile);

  let fileToEncrypt: File;
  if (isVisual) {
    const preparedMedia = await withTimeout(
      prepareConversationMediaFileForUpload(rawFile, { signal }),
      MEDIA_OUTBOX_PREPARE_TIMEOUT_MS,
      t(
        'conversations.mediaPrepareTimeout',
        'Video processing took too long. Check your connection or try a smaller file.',
      ),
    );

    fileToEncrypt = preparedMedia;
    if (options.stripExif && preparedMedia.type.startsWith('image/')) {
      const stripped = await stripExifMetadata(preparedMedia);
      fileToEncrypt = new File([stripped], preparedMedia.name, {
        type: stripped.type || preparedMedia.type,
      });
    }
  } else {
    fileToEncrypt = rawFile;
  }

  const fileBytes = new Uint8Array(await fileToEncrypt.arrayBuffer());
  const mediaKey = randomBytes(32);
  const { ciphertext, nonce } = encryptBytes(mediaKey, fileBytes);
  const encryptedBlob = new Blob([ciphertext.buffer as ArrayBuffer], {
    type: 'application/octet-stream',
  });

  const e2eResult = await withTimeout(
    uploadE2EMediaOnly(api, fileToEncrypt, encryptedBlob, {
      stripExif: options.stripExif && fileToEncrypt.type.startsWith('image/'),
      signal,
      alreadyPrepared: true,
      skipModeration: !options.moderationEnabled || !isVisual,
    }),
    MEDIA_OUTBOX_ATTACHMENT_PIPELINE_TIMEOUT_MS,
    t(
      'conversations.mediaAttachmentTimeout',
      'Processing this attachment took too long. Try again or use a smaller file.',
    ),
  );

  const { moderationScan, ...result } = e2eResult;
  return {
    attachment: {
      e2eMediaId: result.e2eMediaId,
      scanHash: result.scanHash,
      contentType: result.contentType,
      fileName: result.fileName,
      width: result.width,
      height: result.height,
      sizeBytes: result.sizeBytes,
      exifPreserved: result.exifPreserved,
      encryptionKey: toBase64(mediaKey),
      encryptionNonce: toBase64(nonce),
    },
    ...(moderationScan ? { moderationScan } : {}),
  };
}
